import React, { useEffect, useState } from 'react';
import { Store } from 'api/types/store';
import { Button, SelectField, Typography } from 'ui-kit';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { PayoutMethod } from 'api/types/payout';
import { updateStoresPayoutMethod } from 'api/store';
import { message, Input, Divider } from 'antd';
import { Plus, Trash2 } from 'react-feather';
import './PayoutMethods.scss';

export type SplitRecipient = {
  name: string;
  percent: number;
};

type StoreFormItem = {
  id: string | number;
  payoutMethod: string | number;
  recipients: SplitRecipient[];
};

type FormValues = {
  stores: StoreFormItem[];
};

type Props = {
  stores?: Store[];
  payoutMethods?: PayoutMethod[];
  existingRecipients?: string[];
};

const DEFAULT_RECIPIENT_NAME = 'Store Owner';

const normalizeRecipientName = (name: string): string => {
  if (!name) return DEFAULT_RECIPIENT_NAME;
  const trimmed = name.trim();
  const lower = trimmed.toLowerCase();

  if (lower === 'storeowner' || lower === 'store_owner' || lower === 'store owner') {
    return DEFAULT_RECIPIENT_NAME;
  }

  return trimmed
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default function StorePayoutMethodsForm({
  stores,
  payoutMethods,
  existingRecipients = [],
}: Props) {
  const [availableNames, setAvailableNames] = useState<string[]>(() => {
    const initial = new Set([
      DEFAULT_RECIPIENT_NAME,
      ...existingRecipients.map(normalizeRecipientName),
    ]);
    return Array.from(initial);
  });
  const [newNameInput, setNewNameInput] = useState('');

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { isDirty },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: { stores: [] },
  });

  const payoutMethodOptions = payoutMethods
    ?.filter((pm) => pm.isActive)
    .map((pm) => ({
      label: pm.nickname,
      value: pm.id,
    }));

  useEffect(() => {
    if (stores && stores.length > 0) {
      const allDiscoveredNames = new Set<string>([DEFAULT_RECIPIENT_NAME]);

      if (Array.isArray(existingRecipients)) {
        existingRecipients.forEach((recName) => {
          const clean = normalizeRecipientName(recName);
          if (clean) allDiscoveredNames.add(clean);
        });
      }

      const formattedStores = stores.map((store: any) => {
        const splitData: Record<string, any> =
          store.customStoreSplits ??
          store.custom_store_splits ??
          store.payoutSplit ??
          store.payout_split ??
          {};

        const entries = Object.entries(splitData);
        let recipients: SplitRecipient[] = [];

        if (entries.length > 0) {
          recipients = entries.map(([rawName, percent]) => {
            const cleanName = normalizeRecipientName(rawName);
            allDiscoveredNames.add(cleanName);

            return {
              name: cleanName,
              percent: typeof percent === 'number' ? percent : parseFloat(percent) || 0,
            };
          });
        } else {
          recipients = [{ name: DEFAULT_RECIPIENT_NAME, percent: 100 }];
        }

        return {
          id: store.id,
          payoutMethod: store.payoutMethod ?? store.payout_method ?? '',
          recipients,
        };
      });

      const cleanAvailableNames = Array.from(allDiscoveredNames).filter(
        (name) => name !== 'storeOwner' && name !== 'store_owner',
      );

      setAvailableNames(cleanAvailableNames);
      reset({ stores: formattedStores });
    }
  }, [stores, reset]);

  const watchedStores = useWatch({ control, name: 'stores' });

  const handleAddNewNameOption = (storeIndex: number, recipientIndex: number) => {
    const trimmed = newNameInput.trim();
    if (!trimmed) return;

    const normalized = normalizeRecipientName(trimmed);
    const exists = availableNames.includes(normalized);

    if (!exists) {
      setAvailableNames((prev) => [...prev, normalized]);
    }

    setValue(`stores.${storeIndex}.recipients.${recipientIndex}.name`, normalized, {
      shouldDirty: true,
      shouldTouch: true,
    });

    setNewNameInput('');
  };

  const handleDeleteNameOption = (nameToDelete: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (nameToDelete === DEFAULT_RECIPIENT_NAME) {
      message.warning('Cannot delete default recipient Store Owner.');
      return;
    }

    setAvailableNames((prev) => prev.filter((name) => name !== nameToDelete));

    const currentStores = getValues('stores');
    currentStores.forEach((store, sIdx) => {
      store.recipients.forEach((rec, rIdx) => {
        if (rec.name === nameToDelete) {
          setValue(`stores.${sIdx}.recipients.${rIdx}.name`, DEFAULT_RECIPIENT_NAME, {
            shouldDirty: true,
            shouldTouch: true,
          });
        }
      });
    });
  };

  const handleAddRecipientToStore = (storeIndex: number) => {
    const currentRecipients = getValues(`stores.${storeIndex}.recipients`) || [];
    const updated = [...currentRecipients, { name: '', percent: 0 }];
    rebalancePercents(storeIndex, updated);
  };

  const handleRemoveRecipientFromStore = (storeIndex: number, recipientIndex: number) => {
    const currentRecipients = getValues(`stores.${storeIndex}.recipients`) || [];
    if (currentRecipients.length <= 1) {
      message.warning('You must have at least one recipient.');
      return;
    }
    const updated = currentRecipients.filter((_, idx) => idx !== recipientIndex);
    rebalancePercents(storeIndex, updated);
  };

  const handlePercentChange = (storeIndex: number, recipientIndex: number, newValue: number) => {
    const currentRecipients = [...(getValues(`stores.${storeIndex}.recipients`) || [])];
    const safeValue = Math.min(100, Math.max(0, isNaN(newValue) ? 0 : newValue));

    currentRecipients[recipientIndex].percent = safeValue;

    if (currentRecipients.length === 1) {
      currentRecipients[0].percent = 100;
    } else {
      const otherIndices = currentRecipients
        .map((_, idx) => idx)
        .filter((idx) => idx !== recipientIndex);

      const remainingSumNeeded = 100 - safeValue;
      const currentOtherSum = otherIndices.reduce(
        (sum, idx) => sum + currentRecipients[idx].percent,
        0,
      );

      if (currentOtherSum > 0) {
        let allocated = 0;
        otherIndices.forEach((idx, i) => {
          if (i === otherIndices.length - 1) {
            currentRecipients[idx].percent = Number((remainingSumNeeded - allocated).toFixed(2));
          } else {
            const share = (currentRecipients[idx].percent / currentOtherSum) * remainingSumNeeded;
            const roundedShare = Number(share.toFixed(2));
            currentRecipients[idx].percent = roundedShare;
            allocated += roundedShare;
          }
        });
      } else {
        const equalShare = Number((remainingSumNeeded / otherIndices.length).toFixed(2));
        otherIndices.forEach((idx, i) => {
          if (i === otherIndices.length - 1) {
            currentRecipients[idx].percent = Number(
              (remainingSumNeeded - equalShare * (otherIndices.length - 1)).toFixed(2),
            );
          } else {
            currentRecipients[idx].percent = equalShare;
          }
        });
      }
    }

    setValue(`stores.${storeIndex}.recipients`, currentRecipients, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const rebalancePercents = (storeIndex: number, list: SplitRecipient[]) => {
    if (list.length === 0) return;
    const share = Number((100 / list.length).toFixed(2));
    let total = 0;

    const balanced = list.map((item, idx) => {
      if (idx === list.length - 1) {
        return { ...item, percent: Number((100 - total).toFixed(2)) };
      }
      total += share;
      return { ...item, percent: share };
    });

    setValue(`stores.${storeIndex}.recipients`, balanced, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const onSubmit = (data: FormValues) => {
    const payload = data.stores.map((store) => {
      const splitObj: Record<string, number> = {};

      store.recipients.forEach((r) => {
        const cleanName = normalizeRecipientName(r.name);
        if (cleanName) {
          splitObj[cleanName] = r.percent;
        }
      });

      if (Object.keys(splitObj).length === 0) {
        splitObj[DEFAULT_RECIPIENT_NAME] = 100;
      }

      return {
        id: store.id,
        payout_method: store.payoutMethod,
        is_custom_split_active: true,
        custom_store_splits: splitObj,
      };
    });

    updateStoresPayoutMethod({ stores: payload })
      .then((res: any) => {
        if (res?.ok || res?.status === 200 || res?.status === 204) {
          message.success('Store payout settings updated successfully!');
          reset(data);
        } else {
          message.error(
            res?.data?.detail ||
              res?.errors?.nonFields?.[0] ||
              'Something went wrong updating payout settings!',
          );
        }
      })
      .catch((err: any) => {
        message.error(
          err?.response?.data?.detail ||
            err?.message ||
            'Something went wrong updating payout settings!',
        );
      });
  };

  const dropdownOptions = availableNames.map((name) => ({
    label: name,
    value: name,
  }));

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="PayoutMethods_form">
      <div className="PayoutMethods_card">
        <div className="PayoutMethods_header">
          <Typography variant="h5">Store Payout Methods</Typography>
          <Typography variant="body2" className="PayoutMethods_subtitle">
            All payouts from sales go to the store primary payout method.
          </Typography>
          <Typography variant="body2" className="PayoutMethods_link-wrapper">
            <a href="#learn-more" className="PayoutMethods_link">
              Learn more about payouts &rarr;
            </a>
          </Typography>
        </div>

        <div className="PayoutMethods_store-list">
          {stores?.map((store, storeIndex) => {
            const currentFormStore = watchedStores?.[storeIndex];
            const recipients = currentFormStore?.recipients || [];

            const totalPercent = recipients.reduce((acc, r) => acc + (Number(r.percent) || 0), 0);
            const isTotalValid = Math.abs(totalPercent - 100) < 0.01;

            return (
              <div className="PayoutMethods_store-row" key={store.id}>
                <div className="PayoutMethods_store-select">
                  <Typography
                    variant="subtitle1"
                    as="span"
                    className="PayoutMethods_store-select-label"
                  >
                    {store.storeName}
                  </Typography>

                  <Controller
                    control={control}
                    name={`stores.${storeIndex}.payoutMethod`}
                    render={({ field }) => (
                      <SelectField
                        className="PayoutMethods_store-select-input"
                        options={payoutMethodOptions}
                        {...field}
                      />
                    )}
                  />
                </div>

                <div className="PayoutMethods_store-right">
                  <div className="PayoutMethods_split-status-box">
                    <div className="PayoutMethods_split-info">
                      <div className="PayoutMethods_split-status">
                        <Typography variant="body2" className="PayoutMethods_split-status-title">
                          Custom Split
                        </Typography>

                        <div className="PayoutMethods_recipients-wrapper">
                          {recipients.map((recipient, recipientIndex) => (
                            <div
                              key={`${store.id}-${recipientIndex}`}
                              className="PayoutMethods_recipient-row"
                            >
                              <Controller
                                control={control}
                                name={`stores.${storeIndex}.recipients.${recipientIndex}.name`}
                                render={({ field: { onChange, value, ...fieldProps } }) => (
                                  <SelectField
                                    {...fieldProps}
                                    value={value || undefined}
                                    onChange={(val: any) => onChange(val)}
                                    className="PayoutMethods_recipient-select"
                                    options={dropdownOptions}
                                    placeholder="Select or enter recipient"
                                    dropdownRender={() => (
                                      <div className="PayoutMethods_dropdown-menu">
                                        <div className="PayoutMethods_dropdown-options">
                                          {availableNames.length === 0 ? (
                                            <div className="PayoutMethods_dropdown-empty">
                                              No recipients added yet
                                            </div>
                                          ) : (
                                            availableNames.map((name) => (
                                              <div
                                                key={name}
                                                className={`PayoutMethods_dropdown-item ${
                                                  value === name ? 'selected' : ''
                                                }`}
                                                onClick={() => onChange(name)}
                                              >
                                                <span>{name}</span>
                                                {name !== DEFAULT_RECIPIENT_NAME && (
                                                  <button
                                                    type="button"
                                                    className="PayoutMethods_dropdown-delete-btn"
                                                    onClick={(e) => handleDeleteNameOption(name, e)}
                                                    title="Delete recipient"
                                                  >
                                                    <Trash2 size={13} />
                                                  </button>
                                                )}
                                              </div>
                                            ))
                                          )}
                                        </div>

                                        <Divider style={{ margin: '8px 0' }} />

                                        <div
                                          style={{
                                            display: 'flex',
                                            padding: '0 8px 4px',
                                            gap: '8px',
                                          }}
                                        >
                                          <Input
                                            placeholder="Enter custom name..."
                                            value={newNameInput}
                                            onChange={(e) => setNewNameInput(e.target.value)}
                                            onKeyDown={(e) => e.stopPropagation()}
                                          />
                                          <Button
                                            type="button"
                                            variant="contained"
                                            onClick={() =>
                                              handleAddNewNameOption(storeIndex, recipientIndex)
                                            }
                                            style={{ padding: '4px 8px', minWidth: 'auto' }}
                                          >
                                            <Plus size={16} />
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                  />
                                )}
                              />

                              <Controller
                                control={control}
                                name={`stores.${storeIndex}.recipients.${recipientIndex}.percent`}
                                render={({ field }) => (
                                  <input
                                    {...field}
                                    type="number"
                                    step="0.01"
                                    min={0}
                                    max={100}
                                    className="PayoutMethods_percent-input"
                                    value={field.value ?? ''}
                                    onChange={(e) => {
                                      const val = parseFloat(e.target.value);
                                      handlePercentChange(storeIndex, recipientIndex, val);
                                    }}
                                  />
                                )}
                              />
                              <span>%</span>

                              <button
                                type="button"
                                className="PayoutMethods_remove-btn"
                                onClick={() =>
                                  handleRemoveRecipientFromStore(storeIndex, recipientIndex)
                                }
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ))}

                          <div className="PayoutMethods_recipients-footer">
                            <Button
                              type="button"
                              variant="text"
                              className="PayoutMethods_add-btn"
                              onClick={() => handleAddRecipientToStore(storeIndex)}
                            >
                              <Plus size={14} /> Add recipient
                            </Button>

                            <span
                              className={`PayoutMethods_total-badge ${
                                isTotalValid ? 'valid' : 'invalid'
                              }`}
                            >
                              Total: {totalPercent.toFixed(2)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="PayoutMethods_card-footer">
          <Button
            className="PayoutMethods_collapse-button"
            variant="contained"
            type="submit"
            disabled={!isDirty}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </form>
  );
}
